// src/anim/clipLibrary.ts — AnimationClip önbelleği ve CLIPS türevleri (ARCHITECTURE §5.1, ANIMATION §2.4/§8).
// 8 GLB seti: zorunlu CM, GEN, MB, MA, SIM (yükleme ekranı, Promise.all) + tembel CR, SP, TL (maç başladıktan sonra arka planda;
// gelene kadar LAZY_FALLBACK yedekleri: Magic_Shoot → 2H_Stab, Skeletons_Taunt → Cheering, Holding_A#upper → Melee_Blocking#upper).
// GLTF yükleyici ENJEKSİYONLA gelir (`GltfLoaderFn`): tarayıcıda GLTFLoader (animScene / WP9), Node testinde tools/lib/gltf-parse ile
// kurulan AnimationClip'ler — kütüphane fetch/DOM bilmez.
// Türetilmiş klipler (~ @ # +) BİR KEZ üretilir ve aynı Map'e girer; AnimationClip salt veri, tüm aktörler paylaşır (mixer.clipAction
// aktör başına action üretir). `optimize()` yalnız orijinallere, türetmeden ÖNCE (sliceClip örneklediği için güvenli). Süre 0 pozlar 1/30'a.
// Klip adları YALNIZ config/clipTable.ts CLIPS'ten (string literal yasak); clipTable listesinde olmayan klipler kütüphaneye alınmaz.
import * as THREE from 'three';
import {
  ALL_CLIP_NAMES, CLIP_INFO, CLIPS, LAZY_FALLBACK, LAZY_SETS, REQUIRED_SETS, SET_FILES, UPPER_ALLOWED,
  parseClipName, type ClipSet,
} from '../config/clipTable.ts';
import { mirrorClip, collectNodeNames } from './mirror.ts';
import { LOWER, UPPER, makeAdditiveClip, poseClip, sliceClip, splitClip } from './bodyMask.ts';

/** Yükleyicinin döndürmesi gereken en küçük şekil (GLTFLoader sonucu buna uyar). */
export interface GltfClips { animations: THREE.AnimationClip[] }
export type GltfLoaderFn = (url: string) => Promise<GltfClips>;

export interface ClipLibraryOptions {
  /** `#upper` kapısı: kanonik ad → türev üretilsin mi (WP1 clipdata `upperLayerAllowed` ile bağlanır; yoksa UPPER_ALLOWED yeter). */
  allowUpper?: (base: string) => boolean;
  /** AnimationClip.optimize() orijinallere (varsayılan true; testte kapatılabilir). */
  optimize?: boolean;
}

export class ClipLibrary {
  private readonly clips = new Map<string, THREE.AnimationClip>();
  /** Track adlarından toplanan kemik adları (aynalama l↔r takası için). */
  readonly boneNames = new Set<string>();
  readonly loadedSets = new Set<ClipSet>();
  /** resolve() önbelleği: kütüphanede olmayan adlar (kapıyı geçemeyen `#upper`, tembel set gelmemiş) her karede LAZY_FALLBACK arama +
   *  parseClipName tahsisi yapmasın; addSet sonunda temizlenir (tembel set gelince yedekler yeniden çözülür). Sıcak yol yalnız Map.get. */
  private readonly resolveCache = new Map<string, string>();
  private readonly allowUpper: (base: string) => boolean;
  private readonly optimize: boolean;

  constructor(opts: ClipLibraryOptions = {}) {
    this.allowUpper = opts.allowUpper ?? ((base) => UPPER_ALLOWED.has(base));
    this.optimize = opts.optimize ?? true;
  }

  /** Bir setin GLB klipleri: CLIP_INFO'da o sete ait kanonik adlar alınır (fazlası atılır), türevleri üretilir. Eksik kanonik ad → hata. */
  addSet(set: ClipSet, animations: readonly THREE.AnimationClip[]): void {
    const byName = new Map(animations.map((c) => [c.name, c] as const));
    collectNodeNames(animations, this.boneNames);
    for (const [name, info] of Object.entries(CLIP_INFO)) {
      if (info.set !== set) continue;
      const src = byName.get(name);
      if (!src) throw new Error(`clipLibrary: '${name}' ${set} setinde yok`);
      let clip = src;
      if (this.optimize) clip = clip.clone().optimize();   // orijinal GLTF klibine dokunma (başka tüketici olabilir)
      clip = poseClip(clip);                                // süre 0 → 1/30
      clip.name = name;
      this.clips.set(name, clip);
    }
    this.loadedSets.add(set);
    // Bu setin kanonik kliplerine bağlı tüm türetilmiş adlar
    for (const derived of ALL_CLIP_NAMES) {
      const p = parseClipName(derived);
      if (derived === p.base || this.clips.has(derived)) continue;
      if (CLIP_INFO[p.base]?.set !== set) continue;
      if (p.layer === 'upper' && !this.allowUpper(p.base)) continue;   // kapıyı geçemeyen #upper: türev yok (animMap Punch_A#upper'a düşer)
      this.derive(derived);
    }
    this.resolveCache.clear();
  }

  /** Türetilmiş adı üretir: base → (~) → (@t0-t1) → (#layer) → (+). Ara ürünler (ayna) önbelleğe girer. */
  private derive(name: string): THREE.AnimationClip {
    const cached = this.clips.get(name);
    if (cached) return cached;
    const p = parseClipName(name);
    let clip = this.clips.get(p.base);
    if (!clip) throw new Error(`clipLibrary: kanonik '${p.base}' yüklü değil (${name})`);
    let cur = p.base;
    if (p.mirror) {
      cur += '~';
      let m = this.clips.get(cur);
      if (!m) { m = mirrorClip(clip, this.boneNames, cur); this.clips.set(cur, m); }
      clip = m;
    }
    if (p.slice) {
      // ad parçasını özgün dizgeden al (sayı biçimi korunur: '@0-0.65', '@0.25-0.50')
      const at = name.indexOf('@'), end = name.indexOf('#', at) >= 0 ? name.indexOf('#', at) : (name.endsWith('+') ? name.length - 1 : name.length);
      cur += name.slice(at, end);
      let s = this.clips.get(cur);
      if (!s) { s = sliceClip(clip, p.slice[0], p.slice[1], cur); this.clips.set(cur, s); }
      clip = s;
    }
    if (p.layer) {
      cur += '#' + p.layer;
      let l = this.clips.get(cur);
      if (!l) { l = splitClip(clip, p.layer === 'upper' ? UPPER : LOWER, `#${p.layer}`, cur); this.clips.set(cur, l); }
      clip = l;
    }
    if (p.additive) {
      // Hit_A+: katman verilmediyse ÜST gövde ayrımı burada yapılır (split → clone → makeClipAdditive sırası)
      cur += '+';
      const a = p.layer ? (() => { const c = THREE.AnimationUtils.makeClipAdditive(clip.clone(), 0, clip); c.name = cur; return c; })()
        : makeAdditiveClip(clip, cur);
      this.clips.set(cur, a);
      clip = a;
    }
    if (cur !== name) this.clips.set(name, clip);   // biçim farkı güvenliği (ör. '@0-0.65' vs parse edilmiş sayı)
    return clip;
  }

  has(name: string): boolean { return this.clips.has(name); }
  /** Klip; yoksa hata (string literal / yüklenmemiş set yakalanır). */
  get(name: string): THREE.AnimationClip {
    const c = this.clips.get(name);
    if (!c) throw new Error(`clipLibrary: '${name}' yok`);
    return c;
  }
  /** Tembel set gelmemişse yedek ad (LAZY_FALLBACK); kapıyı geçemeyen `#upper` → Punch_A#upper; hiçbiri yoksa ad aynen (get hata verir). */
  resolve(name: string): string {
    if (this.clips.has(name)) return name;
    const cached = this.resolveCache.get(name);
    if (cached !== undefined) return cached;
    const fb = LAZY_FALLBACK[name];
    let r = name;
    if (fb !== undefined && this.clips.has(fb)) r = fb;
    else if (parseClipName(name).layer === 'upper' && this.clips.has(CLIPS.PUNCH_A_UPPER)) r = CLIPS.PUNCH_A_UPPER;
    this.resolveCache.set(name, r);
    return r;
  }
  names(): string[] { return [...this.clips.keys()]; }
  get size(): number { return this.clips.size; }
  isLoaded(set: ClipSet): boolean { return this.loadedSets.has(set); }
}

/** Zorunlu 5 seti paralel yükler; tembel setler `loadLazySets` ile sonra. onProgress(done, total) yükleme ekranı için. */
export async function loadClipLibrary(load: GltfLoaderFn, opts: ClipLibraryOptions & { sets?: readonly ClipSet[]; onProgress?: (done: number, total: number) => void; urlOf?: (path: string) => string } = {}): Promise<ClipLibrary> {
  const lib = new ClipLibrary(opts);
  const sets = opts.sets ?? REQUIRED_SETS;
  const urlOf = opts.urlOf ?? ((p: string): string => p);
  let done = 0;
  const results = await Promise.all(sets.map(async (set) => {
    const g = await load(urlOf(SET_FILES[set]));
    done++; opts.onProgress?.(done, sets.length);
    return [set, g.animations] as const;
  }));
  for (const [set, anims] of results) lib.addSet(set, anims);   // set sırası deterministik (Promise.all sırası)
  return lib;
}

/** Tembel setleri (CR, SP, TL) arka planda tek tek ekler; her set gelince yedekler sessizce yerini bırakır (controller resolve). */
export async function loadLazySets(lib: ClipLibrary, load: GltfLoaderFn, urlOf: (path: string) => string = (p) => p, sets: readonly ClipSet[] = LAZY_SETS): Promise<void> {
  for (const set of sets) {
    if (lib.isLoaded(set)) continue;
    try {
      const g = await load(urlOf(SET_FILES[set]));
      lib.addSet(set, g.animations);
    } catch (e) {
      console.warn(`clipLibrary: tembel set ${set} yüklenemedi, yedekler kalıyor`, e);
    }
  }
}
